import { useQuery } from "@tanstack/react-query";
import { addMonths, format, subDays, subMonths } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import {
  RISK_TIERS,
  TICKET_BUCKETS,
  type CapacityPoint,
  type DecisionRoundView,
  type ProductivitySummary,
  type RiskTier,
  type SparkPoint,
  type TatCell,
  type TicketBucket,
  type TicketMixPoint,
} from "./types";

const MONTHS_TOTAL = 24;
const PRE_MONTHS = 12;

function spark(points: number, start: number, end: number, jitter = 0.4): SparkPoint[] {
  return Array.from({ length: points }, (_, i) => {
    const t = points === 1 ? 1 : i / (points - 1);
    return {
      date: format(subDays(new Date(), points - 1 - i), "yyyy-MM-dd"),
      value: Number((start + (end - start) * t + Math.sin(i * 1.6) * jitter).toFixed(2)),
    };
  });
}

function cell(total: number, tierWeight: number): TatCell {
  const approved = total * 0.34 * tierWeight;
  const funded = total * 0.44 * tierWeight;
  const rejected = total * 0.22 * tierWeight;
  const sum = approved + funded + rejected;
  return {
    approvedHours: Number(approved.toFixed(1)),
    fundedHours: Number(funded.toFixed(1)),
    rejectedHours: Number(rejected.toFixed(1)),
    totalHours: Number(sum.toFixed(1)),
  };
}

/** Relative cost of each tier / bucket — bigger tickets and riskier tiers take longer. */
const TIER_WEIGHT: Record<RiskTier, number> = {
  A: 0.82, B1: 1, B2: 1.24, B3: 1.55, Other: 1.1,
};
const BUCKET_WEIGHT: Record<TicketBucket, number> = {
  "0-10K": 0.6,
  "10-25K": 0.78,
  "25-50K": 0.95,
  "50-100K": 1.15,
  "100-150K": 1.4,
  "150-200K": 1.68,
  "200-250K": 2.05,
  "Over 250K": 2.6,
};

function buildRoundView(kind: "single" | "multi"): DecisionRoundView {
  // Multi-round decisions cost roughly 8-15x a single-round decision.
  const baseHours = kind === "single" ? 6.4 : 6.4 * 11.5;
  const scale = kind === "single" ? 1 : 0.18;

  const tatByTier = RISK_TIERS.map((riskTier) => {
    const weight = TIER_WEIGHT[riskTier];
    const byTicketSize = Object.fromEntries(
      TICKET_BUCKETS.map((bucket) => [bucket, cell(baseHours * BUCKET_WEIGHT[bucket], weight)]),
    ) as Record<TicketBucket, TatCell>;

    return { riskTier, overall: cell(baseHours, weight), byTicketSize };
  });

  const applications = {
    approved: Math.round(921 * scale),
    funded: Math.round(704 * scale),
    rejected: Math.round(561 * scale),
    total: Math.round((921 + 561) * scale),
  };

  return { applications, tatByTier, tatOverall: cell(baseHours, 1.1) };
}

function buildCapacitySeries(officeIsLive: boolean): CapacityPoint[] {
  const start = subMonths(new Date(), MONTHS_TOTAL - 1);

  return Array.from({ length: MONTHS_TOTAL }, (_, i) => {
    const period = format(addMonths(start, i), "yyyy-MM");
    const postIndex = i - PRE_MONTHS;
    const isPost = officeIsLive && postIndex >= 0;
    const t = postIndex < 0 ? 0 : postIndex / (MONTHS_TOTAL - PRE_MONTHS - 1);

    // Non-live offices (and pre-go-live months) hold flat around the base period.
    const volumeIndex = isPost ? 100 + 60 * t : Number((100 + Math.sin(i * 1.1) * 2.2).toFixed(1));
    const headcountIndex = isPost ? 100 + 5 * t : Number((100 + Math.sin(i * 0.7) * 1.4).toFixed(1));

    const applicationsDecided = Math.round(1180 * (volumeIndex / 100));
    const activeHeadcount = Number((14 * (headcountIndex / 100)).toFixed(1));

    // Trend steps at the go-live boundary because the denominator method changes.
    const capacityRatio = isPost
      ? Number((applicationsDecided / activeHeadcount).toFixed(1))
      : Number((84 + Math.sin(i * 0.9) * 2.4).toFixed(1));

    const first = isPost
      ? Number((13.8 - 9.4 * t).toFixed(1))
      : Number((13.8 + Math.sin(i * 1.3) * 0.9).toFixed(1));
    const final = Number((first * (isPost ? 2.1 : 2.35)).toFixed(1));

    return {
      period,
      applicationsDecided,
      decidedByAnalyst: applicationsDecided,
      decidedByAthenaAuto: 0,
      activeHeadcount,
      denominatorMethod: isPost ? "field_touch_multi_attribution" : "historical_single_attribution",
      capacityRatio,
      volumeIndex: Number(volumeIndex.toFixed(1)),
      headcountIndex: Number(headcountIndex.toFixed(1)),
      avgFirstDecisionTimeHours: first,
      avgFinalDecisionTimeHours: final,
    } satisfies CapacityPoint;
  });
}

const MIX_START: Record<TicketBucket, number> = {
  "0-10K": 24,
  "10-25K": 22,
  "25-50K": 18,
  "50-100K": 14,
  "100-150K": 10,
  "150-200K": 7,
  "200-250K": 5,
  "Over 250K": 3,
};
const MIX_END: Record<TicketBucket, number> = {
  "0-10K": 20,
  "10-25K": 21,
  "25-50K": 19,
  "50-100K": 16,
  "100-150K": 11,
  "150-200K": 8,
  "200-250K": 5,
  "Over 250K": 3,
};

function buildTicketMixSeries(officeIsLive: boolean): TicketMixPoint[] {
  const start = subMonths(new Date(), MONTHS_TOTAL - 1);

  return Array.from({ length: MONTHS_TOTAL }, (_, i) => {
    const t = i / (MONTHS_TOTAL - 1);
    const raw = TICKET_BUCKETS.map(
      (bucket) => MIX_START[bucket] + (MIX_END[bucket] - MIX_START[bucket]) * t,
    );
    const sum = raw.reduce((a, b) => a + b, 0);

    const rounded = raw.map((v) => Number(((v / sum) * 100).toFixed(1)));
    // Absorb rounding drift into the largest bucket so shares sum to exactly 100.
    const drift = Number((100 - rounded.reduce((a, b) => a + b, 0)).toFixed(1));
    const largest = rounded.indexOf(Math.max(...rounded));
    rounded[largest] = Number((rounded[largest]! + drift).toFixed(1));

    const mixPct = Object.fromEntries(
      TICKET_BUCKETS.map((bucket, idx) => [bucket, rounded[idx]!]),
    ) as Record<TicketBucket, number>;

    const improving = officeIsLive && i >= PRE_MONTHS;
    const p = improving ? (i - PRE_MONTHS) / (MONTHS_TOTAL - PRE_MONTHS - 1) : 0;

    const decisionTimeWithinBucket = Object.fromEntries(
      TICKET_BUCKETS.map((bucket) => {
        const base = 8 * BUCKET_WEIGHT[bucket];
        return [bucket, Number((base * (1 - 0.42 * p)).toFixed(1))];
      }),
    ) as Record<TicketBucket, number>;

    return { period: format(addMonths(start, i), "yyyy-MM"), mixPct, decisionTimeWithinBucket };
  });
}

function generateProductivitySummary(
  offices: OfficeMapping[],
  selectedOffice: string | undefined,
): ProductivitySummary {
  const isAll = !selectedOffice || selectedOffice === "all";
  const office = offices.find((o) => o.officeName === selectedOffice);
  const officeIsLive = isAll ? offices.some((o) => o.liveOnAthena) : Boolean(office?.liveOnAthena);

  return {
    singleRound: buildRoundView("single"),
    multiRound: buildRoundView("multi"),
    capacitySeries: buildCapacitySeries(officeIsLive),
    ticketMixSeries: buildTicketMixSeries(officeIsLive),
    pullThroughRate: {
      current: officeIsLive ? 76.4 : 71.8,
      previousPeriod: 71.2,
      sparkline: spark(14, 70.6, officeIsLive ? 76.4 : 71.8, 0.5),
    },
    withdrawnRate: {
      current: officeIsLive ? 6.8 : 9.1,
      previousPeriod: 8.6,
      sparkline: spark(14, 9.2, officeIsLive ? 6.8 : 9.1, 0.4),
    },
    athenaProcessingTime: {
      available: false,
      avgHours: null,
      note: "Awaiting timestamp instrumentation from the platform team — tracked as an open item.",
    },
  };
}

export async function fetchProductivitySummary(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean } = {},
): Promise<ProductivitySummary> {
  if (mockFor("/productivity-summary")) return mockResponse(generateProductivitySummary(offices, params.office));
  return apiGet<ProductivitySummary>("/productivity-summary", {
    range: params.range,
    office: params.office,
    compare: params.compare ? "true" : undefined,
  });
}

export function useProductivitySummary(
  params: { range?: string; office?: string; compare?: boolean } = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("productivity-summary", { ...params, offices: officesFingerprint(offices) }),
    queryFn: () => fetchProductivitySummary(offices ?? [], params),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}

/**
 * Re-exported so downstream pages (Dealer and Channel Quality) share one
 * definition of the deal's credit grade rather than keeping a second copy.
 */
export type { RiskTier, TicketBucket } from "./types";
export { RISK_TIERS, TICKET_BUCKETS } from "./types";
